import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { get } from 'lodash'
import { connect } from 'react-redux'
import { compose } from 'recompose'

import { ChangePassword } from 'containers/sections/persons'
import { LeaderTraining, LeaderHistory } from 'containers/forms/leaders'
import { Form } from 'components/layout'
import { DetailLoader } from 'components/loaders'
import { FormButton, FloatingBar } from 'components/misc'
import { Select } from 'components/fields'
import { PersonalBlock, AddressBlock } from 'components/sections/common'
import { apiPersons, apiCodeLists, apiCommon, apiConfiguration } from 'helpers/decorators/api'
import { form, permissions } from 'helpers/decorators'
import { validate } from 'models/persons'
import { asyncValidate } from 'models/persons/asyncValidate'
import { personsDropdownSelector } from 'redux/selectors/persons'
import { CONFIGURATION_KEYS, ORGANIZATION_HIERARCHY } from 'constants/enums'
import { checkEmail } from '../../../redux/modules/api/asyncValidations'

const enhance = compose(
  connect(state => ({
      dropdownPersonLocales: personsDropdownSelector(state)
    }), { checkEmail }),
  apiConfiguration({
    getValues: [
      CONFIGURATION_KEYS.awardStaffRegistrationTitle.id,
      CONFIGURATION_KEYS.awardStaffRegistrationEmailAddress2.id,
      CONFIGURATION_KEYS.awardStaffRegistrationPhone.id,
      CONFIGURATION_KEYS.awardStaffRegistrationMiddleName.id,
      CONFIGURATION_KEYS.awardStaffRegistrationGender.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressLine1.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressLine2.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressCity.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressZipCode.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressRegion.id,
      CONFIGURATION_KEYS.awardStaffRegistrationAddressCountry.id
    ]
  }),
  permissions(),
  apiCodeLists(['genders', 'phoneTypes', 'titles', 'nationalities']),
  apiCommon({
    regions: true
  }),
  apiPersons({
    detail: true,
    locales: true,
    readParams: 'ADDRESS,AWARD_LEADER_PROFILE,AWARD_LEADERSHIP_HISTORY'
  }),
  form({
    form: 'personDetail',
    initialProps: 'persons.detail',
    validate,
    asyncValidate
  })
)

class PersonDetail extends Component {
  static propTypes = {
    pristine: PropTypes.bool,
    submitSucceeded: PropTypes.bool,
    submitFailed: PropTypes.bool,
    invalid: PropTypes.bool,
    codeLists: PropTypes.object.isRequired,
    permissions: PropTypes.object.isRequired,
    common: PropTypes.object.isRequired,
    change: PropTypes.func.isRequired,
    handleSubmit: PropTypes.func.isRequired,
    onPersonUpdate: PropTypes.func.isRequired,
    changePersonPassword: PropTypes.func.isRequired,
    persons: PropTypes.object,
    activeOrganization: PropTypes.object.isRequired,
    dropdownPersonLocales: PropTypes.array,
    onGetConfigurationValue: PropTypes.func.isRequired,
    isOrgConfigurationTrue: PropTypes.func.isRequired
  }

  componentDidMount = async ()=> {
    await this.props.onGetConfigurationValue(CONFIGURATION_KEYS.passwordSpecChars.id, null, true)
  }

  render() {
    const {
      codeLists,
      change,
      persons,
      common,
      pristine,
      submitSucceeded,
      submitFailed,
      invalid,
      onPersonUpdate,
      handleSubmit,
      dropdownPersonLocales,
      permissions: appPermissions,
      activeOrganization
    } = this.props

    const { organizationContacts } = get(persons, 'detail', {})

    const awardLeaderContacts =
      organizationContacts &&
      organizationContacts.filter(item => item.isAwardLeader)[0]

    // check if leader is in organization with default language defined
    const isInNaoWithRegLoc = awardLeaderContacts ? get(awardLeaderContacts, 'organization.defaultLocaleId', 0) > 0 : false

    const passwordSpecChars = get(this.props, `configuration.values.${CONFIGURATION_KEYS.passwordSpecChars.id}.textValue`)

    const organizationId = get(activeOrganization, 'id')

    const showTitle = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationTitle.id)

    const showSecondaryEmail = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationEmailAddress2.id)

    const showPhone = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationPhone.id)

    const showMiddleName = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationMiddleName.id)

    const showAddressLine1 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressLine1.id)

    const showAddressLine2 = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressLine2.id)

    const showCity = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressCity.id)

    const showZipCode = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressZipCode.id)

    const showRegion = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressRegion.id)

    const showCountry = this.props.isOrgConfigurationTrue(organizationId, CONFIGURATION_KEYS.awardStaffRegistrationAddressCountry.id)

    const showAddressBlock = showAddressLine1 || showAddressLine2 || showCity || showZipCode || showRegion || showCountry


    return (
      <Form
        onSubmit={handleSubmit(fields =>
          onPersonUpdate(fields, persons.detail)
        )}
        encType="multipart/form-data"
        loading={persons.loading}
        contentLoader={
          <DetailLoader modulesCount={2} withActionButton withHeavyForm />
        }
        isPerson
      >
        <div className="row">
          <div className="col-xs-12 text--right">
            <ChangePassword
              personId={get(persons, 'detail.id')}
              changePersonPassword={this.props.changePersonPassword}
              passwordSpecChars={passwordSpecChars}
            />
          </div>
        </div>
        <div className="row">
          <div className="col-xs-6">
            <PersonalBlock
              title="Personal Detail"
              intlKey="moduleTitle.personal_details"
              titles={codeLists.titles.list}
              phoneTypes={codeLists.phoneTypes.list}
              change={change}
              data={persons.detail}
              loading={persons.loading}
              disableEmail
              migratedEmail={get(persons, 'detail.migratedContactEmail')}
              decoPersonUpdate="personUpdate"
              canChangeEmail
              personId={get(persons, 'detail.id')}
              showTitle={showTitle}
              showSecondaryEmail={showSecondaryEmail}
              showTertiaryEmail={showSecondaryEmail}
              showMiddleName={showMiddleName}
              showPhone={showPhone}
              showPhoneType
              omitNote
              isOrganizationContact
            />
          </div>
          {appPermissions.isLeader &&
            <div className="row">
              <div className="col-xs-6">
                <LeaderTraining
                  list={get(persons, 'detail.awardLeaderTrainings')}
                  leaderId={get(awardLeaderContacts, 'id')}
                  first
                  hocShouldUpdatePersonDetail
                />
              </div>
              <div className="col-xs-6">
                <LeaderHistory
                  list={get(persons, 'detail.awardLeadershipHistory')}
                  leaderId={get(awardLeaderContacts, 'id')}
                  hocShouldUpdatePersonDetail
                />
              </div>
            </div>}
            <div className="col-xs-6">
           {showAddressBlock && <AddressBlock
              change={change}
              regions={common.regions.list}
              formPrefix="address"
              withCountry
              showAddressLine1={showAddressLine1}
              showAddressLine2={showAddressLine2}
              showCity={showCity}
              showRegion={showRegion}
              showZipCode={showZipCode}
              showCountry={showCountry}
            />}
            <Select
              label="Nationality"
              intlKey="label.nationality"
              name="nationality.value"
              options={codeLists.nationalities.list}
              searchable
            />
            {get(activeOrganization, 'hierarchyLevelType.value') !==
              ORGANIZATION_HIERARCHY.IAC.value && !isInNaoWithRegLoc &&
              <Select
                label="Language settings"
                intlKey="label.language_settings"
                name="locale.id"
                options={dropdownPersonLocales}
                searchable
                loading={persons.loading}
              />}
          </div>
        </div>
        <FloatingBar>
          <FormButton
            pristine={pristine}
            submitSucceeded={submitSucceeded}
            submitFailed={submitFailed}
            invalid={invalid}
            type="submit"
            intlKey="button.save"
          >
            Save
          </FormButton>
        </FloatingBar>
      </Form>
    )
  }
}

export default enhance(PersonDetail)
